// Log de emails transaccionales (fase 6). Vista admin para auditar lo enviado o lo que se
// enviaría si el mailer real estuviera activo. Layout split: tabla a la izquierda + preview
// HTML inline a la derecha (iframe sandbox para no contaminar estilos del panel).

import { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/atoms/Badge';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { ApiError } from '@/services/api';
import {
  getEmail,
  listEmails,
  type EmailFilters,
  type EmailLogDetail,
  type EmailLogItem,
} from '@/services/emails';
import styles from './AdminEmails.module.scss';

const STATUS_TONES: Record<string, 'success' | 'warning' | 'neutral' | 'accent'> = {
  mock: 'neutral',
  queued: 'accent',
  sent: 'success',
  failed: 'warning',
};

export function AdminEmails() {
  const [filters, setFilters] = useState<EmailFilters>({ page: 1, page_size: 50 });
  const [items, setItems] = useState<EmailLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<EmailLogDetail | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const r = await listEmails(filters);
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error al cargar');
    } finally {
      setLoadingList(false);
    }
  }, [filters]);

  useEffect(() => { void reload(); }, [reload]);

  async function selectEmail(id: number) {
    setLoadingDetail(true);
    try {
      const detail = await getEmail(id);
      setSelected(detail);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error al cargar detalle');
    } finally {
      setLoadingDetail(false);
    }
  }

  return (
    <>
      <Header title="Emails transaccionales" />
      <main className={styles.main}>
        <div className={styles.leftCol}>
          <div className={styles.filters}>
            <input
              type="text"
              placeholder="Buscar por email"
              value={filters.recipient ?? ''}
              onChange={(e) => setFilters({ ...filters, recipient: e.target.value || undefined, page: 1 })}
            />
            <select
              value={filters.template ?? ''}
              onChange={(e) => setFilters({ ...filters, template: e.target.value || undefined, page: 1 })}
            >
              <option value="">Todos los templates</option>
              <option value="booking_confirmed">booking_confirmed</option>
              <option value="booking_cancelled">booking_cancelled</option>
              <option value="password_reset">password_reset</option>
              <option value="welcome">welcome</option>
            </select>
            <select
              value={filters.status ?? ''}
              onChange={(e) => setFilters({ ...filters, status: (e.target.value || undefined) as EmailFilters['status'], page: 1 })}
            >
              <option value="">Todos los estados</option>
              <option value="mock">mock</option>
              <option value="sent">sent</option>
              <option value="failed">failed</option>
            </select>
          </div>

          {error && <p className={styles.errorBox}>{error}</p>}

          {loadingList ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner size="lg" /></div>
          ) : items.length === 0 ? (
            <div className={styles.tableWrap}><div className={styles.empty}>No hay emails registrados.</div></div>
          ) : (
            <div className={styles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th><th>Template</th><th>Para</th><th>Asunto</th><th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((e) => {
                    const d = new Date(e.created_at);
                    return (
                      <tr key={e.id} className={selected?.id === e.id ? styles.rowSelected : ''} onClick={() => void selectEmail(e.id)}>
                        <td>{d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                        <td><code style={{ fontSize: 11 }}>{e.template}</code></td>
                        <td>{e.recipient_email}</td>
                        <td>{e.subject}</td>
                        <td><Badge tone={STATUS_TONES[e.status] ?? 'neutral'}>{e.status}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ padding: '8px 16px', fontSize: 12, color: '#666' }}>
                {total} emails en total
              </div>
            </div>
          )}
        </div>

        <div className={styles.rightCol}>
          {loadingDetail ? (
            <div className={styles.preview}><div className={styles.previewEmpty}><Spinner size="lg" /></div></div>
          ) : !selected ? (
            <div className={styles.preview}>
              <div className={styles.previewEmpty}>
                Selecciona un email de la lista para previsualizar.
              </div>
            </div>
          ) : (
            <div className={styles.preview}>
              <header className={styles.previewHeader}>
                <h3>{selected.subject}</h3>
                <dl>
                  <dt>De:</dt><dd>{selected.from_email}</dd>
                  <dt>Para:</dt><dd>{selected.recipient_email}</dd>
                  <dt>Template:</dt><dd><code>{selected.template}</code></dd>
                  <dt>Estado:</dt><dd><Badge tone={STATUS_TONES[selected.status] ?? 'neutral'}>{selected.status}</Badge>{selected.provider && ` · ${selected.provider}`}</dd>
                  {selected.error_message && <><dt>Error:</dt><dd style={{ color: '#991b1b' }}>{selected.error_message}</dd></>}
                </dl>
              </header>
              <div className={styles.previewBody}>
                <iframe srcDoc={selected.html} sandbox="" title="Email preview" />
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
